import { useMemo } from "react";
import {
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";
import { addYears, format } from "date-fns";

import { SelectOptionTypes } from "../../../../components/Input";
import { notify } from "../../../../components/Notification";
import {
  FormPanel,
  discardCopyOf,
  formPanelStatusOf,
} from "../../../../components/FormPanel";
import {
  buildDraftDetails,
  ConfirmDetail,
  formatOptionalCurrency,
} from "../../../../components/PopUp/confirmShared";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import { LiabilityFormFields } from "./LiabilityFormFields";
import {
  AddLiabilityProps,
  CreateLiabilityObject,
  CreateLiabilityPayload,
} from "./types";
import { DEFAULT_LIABILITY, FORM_ID } from "./constants";
import {
  useCreateLiabilityMutation,
  useGetLiabilityTypesQuery,
} from "../../../../services";
import { LiabilityRateTypes } from "../../types";

const PANEL_EYEBROW = "New Liability";
const PANEL_TITLE = "Create liability";
const MOBILE_TITLE = "New liability";
const MOBILE_ACTION_LABEL = "Create";

export const AddLiability = ({ isOpen, onClose }: AddLiabilityProps) => {
  const {
    data: liabilityTypeData,
    isLoading,
    isError,
    refetch,
  } = useGetLiabilityTypesQuery();
  const [createLiabilityTrigger] = useCreateLiabilityMutation();
  const liabilityTypes = useMemo<SelectOptionTypes[]>(
    () =>
      (liabilityTypeData ?? []).map((type) => ({
        label: type.name,
        value: String(type.id),
      })),
    [liabilityTypeData],
  );
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const form = useForm<CreateLiabilityObject>({
    defaultValues: DEFAULT_LIABILITY,
    mode: "onTouched",
    reValidateMode: "onChange",
  });
  const { control, reset, getValues } = form;

  const { isSubmitting } = useFormState({ control });

  const selectedRateType = useWatch({ control, name: "rateType" });
  const isVariableRate =
    selectedRateType?.label === LiabilityRateTypes.VARIABLE;

  const onSubmit: SubmitHandler<CreateLiabilityObject> = async (data) => {
    const startDate = data.startDate ?? new Date();
    const endDate = addYears(startDate, Number(data.term));
    const payload: CreateLiabilityPayload = {
      name: data.name,
      amount: Number.parseFloat(data.amount),
      liabilityTypeId: Number(data.liabilityTypeId?.value),
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
      interestRate: Number.parseFloat(data.interestRate),
      rateType: data.rateType.label,
    };
    if (data.institution) {
      payload.institution = data.institution;
    }
    if (isVariableRate && data.resetFrequency) {
      payload.resetFrequency = Number.parseInt(data.resetFrequency.value, 10);
    }
    if (data.paymentFrequency) {
      payload.paymentFrequency = data.paymentFrequency.value;
    }
    if (data.paymentDueDay) {
      payload.paymentDueDay = data.paymentDueDay;
    }
    if (data.paymentAmount) {
      payload.paymentAmount = Number.parseFloat(data.paymentAmount);
    }
    try {
      await createLiabilityTrigger(payload).unwrap();
      notify.success("Liability created");
      onClose();
      reset(DEFAULT_LIABILITY);
    } catch {
      // Keep the panel open on failure; the global middleware shows the toast.
    }
  };

  const buildDiscardDetails = (): ConfirmDetail => {
    const draft = getValues();
    return buildDraftDetails({
      noun: "liability",
      fieldValues: [
        draft.name,
        draft.institution,
        draft.amount,
        draft.interestRate,
        draft.startDate,
        draft.term,
      ],
      amount: formatOptionalCurrency(draft.amount),
      primaryLabel: draft.name,
    });
  };

  const discard = {
    ...discardCopyOf(false, "liability"),
    details: buildDiscardDetails,
  };

  const load = {
    status: formPanelStatusOf({ isLoading, isError }),
    onRetry: refetch,
    loadingLabel: "Loading liability types…",
    errorMessage: "Failed to load liability types. Please try again.",
  };

  const fields = (
    <LiabilityFormFields
      control={control}
      isSubmitting={isSubmitting}
      isVariableRate={isVariableRate}
      isCompact={isCompact}
      liabilityTypes={liabilityTypes}
    />
  );

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Add liability"
      eyebrow={PANEL_EYEBROW}
      title={PANEL_TITLE}
      form={form}
      resetValues={DEFAULT_LIABILITY}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel="Create liability"
      discard={discard}
      mobile={{ title: MOBILE_TITLE, submitLabel: MOBILE_ACTION_LABEL, fields }}
      load={load}
    >
      {fields}
    </FormPanel>
  );
};
